import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { message, Spin } from "antd";

import LdasApi from "../../../../apis/Ldas.api";
import LdasDetailProductCard from "../detail/LdasDetailProductCard";
import { normalizeLdasListPage } from "../LdasUtils";
import useDeleteLdasProduct from "../useDeleteLdasProduct";

import LdasLotDetailReportSection from "./LdasLotDetailReportSection";
import LdasLotDetailValiditySection from "./LdasLotDetailValiditySection";

const REPORT_PAGE_SIZE = 5;
const CERT_PAGE_SIZE = 5;

const makeEmptyReportPage = () => ({
  content: [],
  pageable: { pageNumber: 0, pageSize: REPORT_PAGE_SIZE },
  totalElements: 0,
});

const makeEmptyCertPage = () => ({
  content: [],
  pageable: { pageNumber: 0, pageSize: CERT_PAGE_SIZE },
  totalElements: 0,
});

const LdasLotDetailComponent = () => {
  const history = useHistory();
  const { productId, lotId } = useParams();
  const { deleteLdasProductWithConfirm } = useDeleteLdasProduct();

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [lot, setLot] = useState(null);
  const [reportListPage, setReportListPage] = useState(makeEmptyReportPage);
  const [certListPage, setCertListPage] = useState(makeEmptyCertPage);

  const initialReportSigRef = useRef("");
  const initialCertSigRef = useRef("");

  /** product·lot·reports·certs 초기 병렬 로드 (리포트·인증은 0페이지) */
  const fetchLdasLotDetailInitial = async ({
    productId,
    lotId,
    history,
    setProduct,
    setLot,
    setReportListPage,
    setCertListPage,
    initialReportSigRef,
    initialCertSigRef,
  }) => {
    initialReportSigRef.current = "";
    initialCertSigRef.current = "";

    let productRes;
    let lotRes;
    let reportRes;
    let certRes;

    try {
      [productRes, lotRes, reportRes, certRes] = await Promise.all([
        LdasApi.readLdasProductDetail(productId),
        LdasApi.readLdasLotDetail(lotId),
        LdasApi.readLdasTestReportListByLot(lotId, {
          page: 0,
          size: REPORT_PAGE_SIZE,
        }),
        LdasApi.readLdasCertificationListByLot(lotId, {
          page: 0,
          size: CERT_PAGE_SIZE,
        }),
      ]);
    } catch (e) {
      console.error("loadLotDetail", e);
      message.error("데이터를 불러올 수 없습니다");
      history.replace("/ldas/list");
      return;
    }

    if (productRes?.code !== 200) {
      message.error(productRes?.message ?? "제품을 불러올 수 없습니다");
      history.replace("/ldas/list");
      return;
    }
    setProduct(productRes.result);

    if (lotRes?.code !== 200) {
      message.error(lotRes?.message ?? "Lot 정보를 불러올 수 없습니다");
      history.replace("/ldas/list");
      return;
    }
    setLot(lotRes.result);

    if (reportRes?.code === 200) {
      setReportListPage(
        normalizeLdasListPage(reportRes, {
          pageNumber: 0,
          pageSize: REPORT_PAGE_SIZE,
        }),
      );
    } else {
      message.error(
        reportRes?.message ?? "테스트 리포트 목록을 불러올 수 없습니다",
      );
    }

    if (certRes?.code === 200) {
      setCertListPage(
        normalizeLdasListPage(certRes, {
          pageNumber: 0,
          pageSize: CERT_PAGE_SIZE,
        }),
      );
    } else {
      message.error(certRes?.message ?? "인증 목록을 불러올 수 없습니다");
    }

    initialReportSigRef.current = `${lotId}\t0\t${REPORT_PAGE_SIZE}`;
    initialCertSigRef.current = `${lotId}\t0\t${CERT_PAGE_SIZE}`;
  };

  useEffect(() => {
    if (!productId || !lotId) return;

    setLoading(true);
    void fetchLdasLotDetailInitial({
      productId,
      lotId,
      history,
      setProduct,
      setLot,
      setReportListPage,
      setCertListPage,
      initialReportSigRef,
      initialCertSigRef,
    }).finally(() => setLoading(false));
  }, [productId, lotId, history]);

  const refetchReports = useCallback(
    async (pageNumber, pageSize) => {
      const res = await LdasApi.readLdasTestReportListByLot(lotId, {
        page: pageNumber,
        size: pageSize,
      });
      if (res?.code === 200) {
        setReportListPage((prev) => ({
          ...prev,
          ...normalizeLdasListPage(res, { pageNumber, pageSize }),
        }));
      } else {
        message.error(
          res?.message ?? "테스트 리포트 목록을 불러올 수 없습니다",
        );
      }
    },
    [lotId],
  );

  const refetchCerts = useCallback(
    async (pageNumber, pageSize) => {
      const res = await LdasApi.readLdasCertificationListByLot(lotId, {
        page: pageNumber,
        size: pageSize,
      });
      if (res?.code === 200) {
        setCertListPage((prev) => ({
          ...prev,
          ...normalizeLdasListPage(res, { pageNumber, pageSize }),
        }));
      } else {
        message.error(res?.message ?? "인증 목록을 불러올 수 없습니다");
      }
    },
    [lotId],
  );

  /** 생성·수정 API의 res.result로 목록 갱신 (목록 재조회 생략) */
  const mergeCertListAfterSave = useCallback((saved) => {
    setCertListPage((prev) => {
      if (!saved || saved.id === undefined || saved.id === null) {
        return prev;
      }
      const { pageNumber, pageSize } = prev.pageable;
      const idx = prev.content.findIndex((c) => c.id === saved.id);
      if (idx >= 0) {
        const nextContent = [...prev.content];
        nextContent[idx] = { ...nextContent[idx], ...saved };
        return { ...prev, content: nextContent };
      }
      if (pageNumber === 0) {
        return {
          ...prev,
          content: [saved, ...prev.content].slice(0, pageSize),
          totalElements: prev.totalElements + 1,
        };
      }
      return {
        ...prev,
        totalElements: prev.totalElements + 1,
      };
    });
  }, []);

  // 리포트 페이지 변경 시 refetch
  useEffect(() => {
    if (!lotId) return;
    const { pageNumber, pageSize } = reportListPage.pageable;
    const sig = `${lotId}\t${pageNumber}\t${pageSize}`;
    if (sig === initialReportSigRef.current) {
      initialReportSigRef.current = "";
      return;
    }
    void refetchReports(pageNumber, pageSize);
  }, [
    lotId,
    reportListPage.pageable.pageNumber,
    reportListPage.pageable.pageSize,
    refetchReports,
  ]);

  // 인증 페이지 변경 시 refetch
  useEffect(() => {
    if (!lotId) return;
    const { pageNumber, pageSize } = certListPage.pageable;
    const sig = `${lotId}\t${pageNumber}\t${pageSize}`;
    if (sig === initialCertSigRef.current) {
      initialCertSigRef.current = "";
      return;
    }
    void refetchCerts(pageNumber, pageSize);
  }, [
    lotId,
    certListPage.pageable.pageNumber,
    certListPage.pageable.pageSize,
    refetchCerts,
  ]);

  const handleReportPaginationChange = (nextPage, nextPageSize) => {
    setReportListPage((prev) => {
      const size = nextPageSize ?? prev.pageable.pageSize;
      return {
        ...prev,
        pageable: {
          pageNumber: size !== prev.pageable.pageSize ? 0 : nextPage - 1,
          pageSize: size,
        },
      };
    });
  };

  const handleCertPaginationChange = (nextPage, nextPageSize) => {
    setCertListPage((prev) => {
      const size = nextPageSize ?? prev.pageable.pageSize;
      return {
        ...prev,
        pageable: {
          pageNumber: size !== prev.pageable.pageSize ? 0 : nextPage - 1,
          pageSize: size,
        },
      };
    });
  };

  const handleEdit = () => {
    history.push(`/ldas/edit/${productId}`);
  };

  const handleDelete = () => {
    void deleteLdasProductWithConfirm({
      productId,
      productName: product?.productName,
      imageKey: product?.imageUrl,
    });
  };

  if (loading) {
    return (
      <div className="ldas-page__content ldas-page__content--detail-loading">
        <Spin size="large" tip="불러오는 중..." />
      </div>
    );
  }

  if (!product || !lot) return null;

  return (
    <div className="ldas-page__content">
      <div className="ldas-detail-header">
        <div className="ldas-detail-breadcrumb">
          <span className="ldas-detail-breadcrumb__prev">제품 관리</span>
          <span className="ldas-detail-breadcrumb__sep">›</span>
          <span
            className="ldas-detail-breadcrumb__prev ldas-detail-breadcrumb__prev--link"
            onClick={() => history.goBack()}
          >
            {product.productName}
          </span>
          <span className="ldas-detail-breadcrumb__sep">›</span>
          <span className="ldas-detail-breadcrumb__cur">{lot.lotNumber}</span>
        </div>
        <div className="ldas-detail-header__actions">
          <button
            type="button"
            className="ldas-detail-btn ldas-detail-btn--edit"
            onClick={handleEdit}
          >
            수정
          </button>
          <button
            type="button"
            className="ldas-detail-btn ldas-detail-btn--delete"
            onClick={handleDelete}
          >
            삭제
          </button>
        </div>
      </div>

      <div className="ldas-detail-layout">
        <LdasDetailProductCard
          product={product}
          lot={lot}
          certCount={certListPage.totalElements}
          reportCount={reportListPage.totalElements}
        />

        <div className="ldas-detail-right-sections">
          {/* ✅ 인증 섹션 */}
          <LdasLotDetailValiditySection
            certs={certListPage.content}
            certCount={certListPage.totalElements}
            lotId={lotId}
            onCertMerged={mergeCertListAfterSave}
            onRefetch={() =>
              refetchCerts(
                certListPage.pageable.pageNumber,
                certListPage.pageable.pageSize,
              )
            }
            pagination={{
              current: certListPage.pageable.pageNumber + 1,
              pageSize: certListPage.pageable.pageSize,
              total: certListPage.totalElements,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} / 총 ${total}건`,
              hideOnSinglePage: false,
              showSizeChanger: false,
              onChange: handleCertPaginationChange,
            }}
          />
          {/* 🧪 테스트 리포트 섹션 */}
          <LdasLotDetailReportSection
            reports={reportListPage.content}
            reportCount={reportListPage.totalElements}
            lotId={lotId}
            onRefetch={() =>
              refetchReports(
                reportListPage.pageable.pageNumber,
                reportListPage.pageable.pageSize,
              )
            }
            pagination={{
              current: reportListPage.pageable.pageNumber + 1,
              pageSize: reportListPage.pageable.pageSize,
              total: reportListPage.totalElements,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} / 총 ${total}건`,
              hideOnSinglePage: false,
              showSizeChanger: false,
              onChange: handleReportPaginationChange,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LdasLotDetailComponent;
